/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import { DeviceBrandDto } from 'api/deviceBrands/models/DeviceBrandDto';
import UsersService from 'api/users/UsersService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Button, Card, Container, Dropdown, Form, Stack } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './DeviceBrandsScreen.module.scss';
import DeviceBrandsService from 'api/deviceBrands/DeviceBrandsService';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import { Paged } from 'api/common/types/Page';
import { DeviceBrandSearchCriteria } from 'api/deviceBrands/models/DeviceBrandSearchCriteria';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';

type Props = {
};

const DeviceBrandsScreen: React.FC<Props> = () => {
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasDeviceBrandsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['CONFIG_DEVICE_BRAND_WRITE']);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [criteria, setCriteria] = useState<DeviceBrandSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'asc',
        orderColumn: 'db.name'
    });
    const [filters, setFilters] = useState({ name: '' });
    const [deviceBrandsPage, setDeviceBrandsPage] = useState<Paged<DeviceBrandDto>>();
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<DeviceBrandDto | null>(null);

    const showRemoveItemDialog = async (item: DeviceBrandDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }
        try {
            await DeviceBrandsService.remove(itemToRemove);
            onCancelRemove();
            toast.success(t('messages.record_delete_success'));
            setCriteria({ ...criteria, page: 1 });
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete device brands', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const renderTableActionCell = (row: DeviceBrandDto) => {
        return <Dropdown>
            <Dropdown.Toggle id="dropdown-basic-button" size="sm" variant="outline-secondary">...</Dropdown.Toggle>
            <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount>
                <Dropdown.Item onClick={() => navigate(`/devicebrand/details/${row.id}`, { state: { row } })}>{t('common.details')}</Dropdown.Item>
                {hasDeviceBrandsWritePolicy && <>
                    <Dropdown.Item onClick={() => navigate(`/devicebrand/edit/${row.id}`, { state: { row } })}>{t('common.edit')}</Dropdown.Item>
                    <Dropdown.Item onClick={() => showRemoveItemDialog(row)}>{t('common.delete')}</Dropdown.Item>
                </>
                }
            </Dropdown.Menu>
        </Dropdown>
    };

    useEffect(() => {
        search().catch(console.error)
    }, [criteria]);

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const onRowClick = (event: any, row?: DeviceBrandDto) => {
        if (row?.id) {
            if (event.ctrlKey) {
                window.open('/devicebrand/details/' + row.id, '_blank');
                return;
            }
            navigate(`/devicebrand/details/${row.id}`, { state: { row } });
        }
    }

    function handleFilterChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFilters({ ...filters, [key]: value });
    }

    const onSearch = () => {
        setCriteria({ ...criteria, page: 1, name: filters.name });
    }

    const clearFilters = () => {
        setFilters({ name: '' });
        setCriteria({ ...criteria, page: 1, name: '' });
    }

    const tableColumns: ListingTableColumn<DeviceBrandDto>[] = [
        {
            field: 'name',
            name: t('config_devices.brands.list.name') ?? '',
            onSearch: onTableFilter,
            searchField: 'db.name'
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' }
        },
    ];

    const search = async () => {
        try {
            Loading.show();
            const page = await DeviceBrandsService.getList(criteria);
            setDeviceBrandsPage(page);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get device brand list', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    return (<>
        <Container fluid>
            <Card className={styles.filters}>
                <Card.Body>
                    <Stack direction="horizontal" gap={3}>
                        <Form.Control type="text" value={filters.name} className="me-auto" name="name" onChange={handleFilterChange} onKeyDown={(e: any) => { if (e.key === 'Enter') onSearch(); }} placeholder={t('config_devices.brands.list.name')} />
                        <Button variant="secondary" onClick={onSearch}>{t('common.search')}</Button>
                        <div className="vr" />
                        <Button variant="outline-danger" onClick={clearFilters}>{t('common.clear')}</Button>
                        {hasDeviceBrandsWritePolicy && <Button variant="primary" onClick={() => navigate('/devicebrand/new/')}>{t('common.new')}</Button>}
                    </Stack>
                </Card.Body>
            </Card>
        </Container>
        <Container fluid>
            <ListingTable
                columns={tableColumns}
                rows={deviceBrandsPage?.items || []}
                onRowClick={(row, _, event) => onRowClick(event, row)}
                allowHover={true}
                initialSearch={{ colField: 'db.name', isOrderAsc: true }}
            />
            <PaginationWithInfo
                itemName={t('config_devices.brands.title')}
                currentPage={deviceBrandsPage?.currentPage ?? 1}
                pageItems={deviceBrandsPage?.items.length || 0}
                totalItems={deviceBrandsPage?.totalItems || 0}
                onChange={page => setCriteria({ ...criteria, page })} />
        </Container>
        <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: itemToRemove?.name ?? '' })} />
    </>);
};

export default DeviceBrandsScreen;
